import { useEffect, useState } from 'react';
import frappe from './frappe';
import { fetchStockWithLabels, fetchAllStock } from './getStocks';
import { getVariantsWithValues } from './getVariants';

const FRAPPE_BASE = 'https://volteonelectronics.v.frappe.cloud';

const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  return `${FRAPPE_BASE}${imagePath}`;
};

const ITEM_FIELDS = '["name","item_name","item_group","standard_rate","image","description","has_variants","is_sales_item"]';
const BASE_FILTERS = '[["is_sales_item","=",1],["disabled","=",0],["variant_of","=",""]]';

const filterProductsByQuery = (products, query) => {
  if (!query || query.trim() === '') return products;
  const q = query.toLowerCase();
  return products
    .filter(p =>
      (p.label && p.label.toLowerCase().includes(q)) ||
      (p.description && p.description.toLowerCase().includes(q))
    )
    .sort((a, b) => {
      const aIdx = (a.label || '').toLowerCase().indexOf(q);
      const bIdx = (b.label || '').toLowerCase().indexOf(q);
      if (aIdx === 0 && bIdx !== 0) return -1;
      if (bIdx === 0 && aIdx !== 0) return 1;
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
};

const toProductShape = (item, stockByItem = {}) => ({
  label: item.item_name,
  description: item.description || '',
  price: item.standard_rate,
  stock_reel: stockByItem[item.name] || [],
  id: item.name,
  tag: '',
  ref: item.name,
  img: buildImageUrl(item.image),
});

// Novedades e Insignia pendiente de configuración en Frappe
const fetchNovedadesProducts = async () => [];
export const fetchInsigniaProducts = async () => [];

export const obtenerLinkShare = async (ref) => {
  // En Frappe la imagen viene directo en el Item; no hay sistema de share hash
  try {
    const { data } = await frappe.get(`/api/resource/Item/${ref}?fields=["image"]`);
    return data.data.image || null;
  } catch {
    return null;
  }
};

export const fetchProducts = async (update, id, query) => {
  try {
    let filters;
    if (!update) {
      filters = BASE_FILTERS;
    } else {
      filters = `[["is_sales_item","=",1],["disabled","=",0],["variant_of","=",""],["item_group","=","${id}"]]`;
    }

    const { data } = await frappe.get(
      `/api/resource/Item?filters=${encodeURIComponent(filters)}&fields=${encodeURIComponent(ITEM_FIELDS)}&limit=1000`
    );

    const [novedadesIds, insigniaIds, stockByItem] = await Promise.all([
      fetchNovedadesProducts(),
      fetchInsigniaProducts(),
      fetchAllStock(),
    ]);

    const products = data.data.map(item => {
      const p = toProductShape(item, stockByItem);
      if (insigniaIds.includes(item.name)) p.tag = 'Insignia';
      else if (novedadesIds.includes(item.name)) p.tag = 'Nuevo';
      return p;
    });

    return filterProductsByQuery(products, query);
  } catch (error) {
    console.error('Error obteniendo productos:', error);
    return [];
  }
};

export const fetchProductById = async (id) => {
  try {
    const { data } = await frappe.get(`/api/resource/Item/${id}`);
    const item = data.data;

    const [variants, stocks] = await Promise.all([
      getVariantsWithValues(id),
      fetchStockWithLabels(id),
    ]);

    return {
      label: item.item_name,
      price: item.standard_rate,
      stock_reel: stocks || '',
      image: buildImageUrl(item.image),
      description: item.description || '',
      variants,
    };
  } catch (error) {
    console.error('Error obteniendo producto por id:', error);
    return false;
  }
};

export function useFetchProduct(id) {
  const [product, setProduct] = useState([]);
  useEffect(() => {
    if (!id) return;
    fetchProductById(id).then(setProduct);
  }, [id]);
  return product;
}

export const countProducts = async (update, categoryID, query) => {
  try {
    let filters;
    if (!update) {
      filters = BASE_FILTERS;
    } else {
      filters = `[["is_sales_item","=",1],["disabled","=",0],["variant_of","=",""],["item_group","=","${categoryID}"]]`;
    }

    const { data } = await frappe.get(
      `/api/resource/Item?filters=${encodeURIComponent(filters)}&fields=["name","item_name","description"]&limit=1000`
    );

    const allProducts = data.data.map(item => ({
      id: item.name,
      label: item.item_name,
      description: item.description || '',
    }));

    const filtered = query
      ? filterProductsByQuery(allProducts, query)
      : allProducts;

    return {
      totalProducts: filtered.length,
      finalProducts: filtered.map(p => p.id),
    };
  } catch (error) {
    console.error('Error al obtener IDs de productos:', error.message);
    return { totalProducts: 0, finalProducts: [] };
  }
};

export const groupProductIDs = async (update, categoryId, query) => {
  try {
    const { totalProducts, finalProducts } = await countProducts(update, categoryId, query);
    const groups = [];
    for (let i = 0; i < totalProducts; i += 9) {
      groups.push(finalProducts.slice(i, i + 9));
    }
    return { totalGroups: groups.length, groups };
  } catch (error) {
    console.error('Error al agrupar IDs de productos:', error.message);
    return { totalGroups: 0, groups: [] };
  }
};

export const fetchProductsByGroup = async (group) => {
  try {
    if (!group || group.length === 0) return [];

    // Fetch en paralelo todos los items del grupo
    const requests = group.map(itemCode =>
      frappe.get(
        `/api/resource/Item/${itemCode}?fields=${encodeURIComponent(ITEM_FIELDS)}`
      ).catch(() => null)
    );

    const [responses, stockByItem] = await Promise.all([
      Promise.all(requests),
      fetchAllStock(),
    ]);
    return responses
      .filter(r => r !== null)
      .map(r => toProductShape(r.data.data, stockByItem));
  } catch (error) {
    console.error('Error al obtener productos por grupo:', error.message);
    return [];
  }
};

export const fetchImages = async (products) => {
  // En Frappe la imagen ya viene en el campo image del Item
  return products.map(p => ({
    ...p,
    img: p.img || '',
  }));
};

export const fetchImage = async (id) => {
  try {
    const { data } = await frappe.get(`/api/resource/Item/${id}?fields=["image"]`);
    return buildImageUrl(data.data.image);
  } catch (error) {
    console.error(`Error obteniendo imagen de ${id}:`, error);
    return '';
  }
};
